import React, { useState, useEffect } from 'react';
import { X, Undo2, AlertCircle } from 'lucide-react';
import { episService } from '../../../services/epis.service';
import { colaboradoresService } from '../../../services/colaboradores.service';
import SearchableSelect from '../../../components/SearchableSelect';
import type {
  RegistrarDevolucaoPayload,
  EpiDevolucaoEstadoConservacao,
  EpiDevolucaoDestinoEstoque,
  EpiDevolucaoMotivo,
} from '@sstexames/shared-types';

interface ColaboradorOption {
  id: number;
  nome: string;
  cpf: string;
  cargo_nome?: string;
  setor_nome?: string;
}

interface ItemDevolucaoForm {
  entrega_item_id: number;
  descricao_epi: string;
  ca_numero?: string | null;
  fabricante?: string | null;
  quantidade: number;
  selecionado: boolean;
  estado_conservacao: EpiDevolucaoEstadoConservacao;
  destino_estoque: EpiDevolucaoDestinoEstoque;
  motivo: EpiDevolucaoMotivo;
  observacoes?: string;
}

interface DevolucaoEpiModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  empresaId: number;
  initialColaboradorId?: number;
  initialMotivo?: EpiDevolucaoMotivo;
  initialAgendamentoId?: number;
}

export const DevolucaoEpiModal: React.FC<DevolucaoEpiModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  empresaId,
  initialColaboradorId,
  initialMotivo = 'demissao_automatica',
  initialAgendamentoId,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingItens, setLoadingItens] = useState(false);
  const [colaboradores, setColaboradores] = useState<ColaboradorOption[]>([]);
  const [selectedColaboradorId, setSelectedColaboradorId] = useState<number | ''>(initialColaboradorId || '');
  const [itensColaborador, setItensColaborador] = useState<ItemDevolucaoForm[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Load Colaboradores
  useEffect(() => {
    if (!isOpen || !empresaId) return;

    const fetchColabs = async () => {
      try {
        const colabsRes = await colaboradoresService.findAll({
          limit: 200,
        });

        const colabList: ColaboradorOption[] = (colabsRes?.data || []).map((c) => ({
          id: c.id,
          nome: c.nome,
          cpf: c.cpf,
          cargo_nome: c.cargo?.nome || 'Sem Cargo',
          setor_nome: c.setor?.nome || 'Sem Setor',
        }));
        setColaboradores(colabList);
      } catch (err) {
        console.error('Erro ao carregar colaboradores:', err);
      }
    };

    fetchColabs();
  }, [isOpen, empresaId]);

  // When colaborador is selected, fetch active deliveries to get his currently held EPIs
  useEffect(() => {
    if (!selectedColaboradorId || !empresaId) {
      setItensColaborador([]);
      return;
    }

    const fetchItens = async () => {
      try {
        setLoadingItens(true);
        // Utilizar cronograma de vigências filtrado pelo colaborador
        const vigencias = await episService.getCronogramaVigencias(empresaId);
        const colabItens = vigencias.filter((v) => v.colaborador_id === Number(selectedColaboradorId));

        setItensColaborador(
          colabItens.map((v) => ({
            entrega_item_id: v.entrega_item_id,
            descricao_epi: v.descricao_epi || v.epi_descricao || 'EPI',
            ca_numero: v.ca_numero,
            fabricante: v.fabricante || null,
            quantidade: v.quantidade || 1,
            selecionado: true,
            estado_conservacao: 'reaproveitavel',
            destino_estoque: 'retornou_estoque',
            motivo: initialMotivo,
            observacoes: '',
          })),
        );
      } catch (err) {
        console.error('Erro ao carregar equipamentos do colaborador:', err);
      } finally {
        setLoadingItens(false);
      }
    };

    fetchItens();
  }, [selectedColaboradorId, empresaId, initialMotivo]);

  const handleToggleItem = (index: number) => {
    setItensColaborador((prev) =>
      prev.map((it, i) => (i === index ? { ...it, selecionado: !it.selecionado } : it)),
    );
  };

  const handleUpdateItem = (
    index: number,
    field: keyof ItemDevolucaoForm,
    value: string | boolean | number,
  ) => {
    setItensColaborador((prev) =>
      prev.map((it, i) => {
        if (i !== index) return it;
        const updated = { ...it, [field]: value };

        // Ajustar destino de estoque automaticamente baseado no estado de conservação
        if (field === 'estado_conservacao') {
          if (value === 'reaproveitavel') {
            updated.destino_estoque = 'retornou_estoque';
          } else if (value === 'avariado_descarte') {
            updated.destino_estoque = 'descarte_baixa';
          } else if (value === 'nao_devolvido') {
            updated.destino_estoque = 'sem_movimentacao_fisica';
          }
        }
        return updated;
      }),
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    const itensSelecionados = itensColaborador.filter((it) => it.selecionado);
    if (itensSelecionados.length === 0) {
      setErrorMessage('Selecione pelo menos um EPI para registrar a devolução.');
      return;
    }

    try {
      setLoading(true);
      const payload: RegistrarDevolucaoPayload = {
        empresa_id: empresaId,
        colaborador_id: Number(selectedColaboradorId),
        agendamento_id: initialAgendamentoId,
        itens: itensSelecionados.map((it) => ({
          entrega_item_id: it.entrega_item_id,
          estado_conservacao: it.estado_conservacao,
          destino_estoque: it.destino_estoque,
          motivo: it.motivo,
          observacoes: it.observacoes || undefined,
        })),
      };

      await episService.registrarDevolucao(payload);
      onSuccess();
      onClose();
    } catch (err: unknown) {
      console.error('Erro ao registrar devolução:', err);
      const errorMsg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao registrar devolução dos equipamentos.';
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center border border-amber-100">
              <Undo2 className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Registrar Devolução / Recolhimento de EPI</h3>
              <p className="text-xs text-gray-500">Inspeção técnica e conferência para retorno ao estoque ou baixa</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
          {errorMessage && (
            <div className="bg-rose-50 border border-rose-200 text-rose-800 px-4 py-2.5 rounded-xl text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div>
            <SearchableSelect
              label="Colaborador"
              required
              options={colaboradores.map((colab) => ({
                id: colab.id,
                label: `${colab.nome} - ${colab.cargo_nome || 'Sem cargo'} (${colab.cpf})`,
              }))}
              value={selectedColaboradorId || null}
              onChange={(val) => setSelectedColaboradorId(val ? Number(val) : '')}
              placeholder="Selecione o colaborador..."
              searchPlaceholder="Buscar por nome, cargo ou CPF..."
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
              Equipamentos com o Colaborador para Devolução
            </label>

            {loadingItens ? (
              <div className="p-8 text-center text-xs text-gray-400">Carregando equipamentos em posse...</div>
            ) : itensColaborador.length === 0 ? (
              <div className="p-6 border border-dashed border-gray-200 rounded-xl text-center text-xs text-gray-400">
                Nenhum EPI ativo vinculado a este colaborador no momento.
              </div>
            ) : (
              <div className="space-y-3">
                {itensColaborador.map((item, idx) => (
                  <div
                    key={idx}
                    className={`p-3.5 rounded-xl border transition-all ${
                      item.selecionado
                        ? 'border-amber-300 bg-amber-50/20'
                        : 'border-gray-200 bg-gray-50/60 opacity-60'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2.5">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={item.selecionado}
                          onChange={() => handleToggleItem(idx)}
                          className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                        />
                        <span className="text-xs font-bold text-gray-900">{item.descricao_epi}</span>
                      </label>
                      <span className="text-[11px] text-gray-500 font-mono">
                        CA: {item.ca_numero || 'N/A'} (Qtd: {item.quantidade})
                      </span>
                    </div>

                    {item.selecionado && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2.5 border-t border-gray-100">
                        <div>
                          <SearchableSelect
                            label="Estado de Conservação"
                            required
                            options={[
                              { id: 'reaproveitavel', label: 'Higienizado / Íntegro' },
                              { id: 'avariado_descarte', label: 'Danificado / Descarte' },
                              { id: 'nao_devolvido', label: 'Não Devolvido / Extraviado' },
                            ]}
                            value={item.estado_conservacao}
                            onChange={(val) =>
                              val &&
                              handleUpdateItem(
                                idx,
                                'estado_conservacao',
                                val as EpiDevolucaoEstadoConservacao,
                              )
                            }
                            placeholder="Selecione o estado..."
                            searchPlaceholder="Buscar estado..."
                          />
                        </div>

                        <div>
                          <SearchableSelect
                            label="Destino de Estoque"
                            required
                            options={[
                              { id: 'retornou_estoque', label: 'Volta ao Estoque Disponível' },
                              { id: 'descarte_baixa', label: 'Enviar para Descarte' },
                              { id: 'sem_movimentacao_fisica', label: 'Sem Entrada Física' },
                            ]}
                            value={item.destino_estoque}
                            onChange={(val) =>
                              val &&
                              handleUpdateItem(
                                idx,
                                'destino_estoque',
                                val as EpiDevolucaoDestinoEstoque,
                              )
                            }
                            placeholder="Selecione o destino..."
                            searchPlaceholder="Buscar destino..."
                          />
                        </div>

                        <div>
                          <SearchableSelect
                            label="Motivo"
                            options={[
                              { id: 'demissao_automatica', label: 'Exame Demissional' },
                              { id: 'demissao_manual', label: 'Desligamento Manual' },
                              { id: 'troca_periodica', label: 'Troca Periódica' },
                              { id: 'avaria', label: 'Avaria em Operação' },
                              { id: 'mudanca_funcao', label: 'Mudança de Função' },
                              { id: 'outro', label: 'Outro Motivo' },
                            ]}
                            value={item.motivo}
                            onChange={(val) =>
                              val &&
                              handleUpdateItem(idx, 'motivo', val as EpiDevolucaoMotivo)
                            }
                            placeholder="Selecione o motivo..."
                            searchPlaceholder="Buscar motivo..."
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-gray-100 pt-4 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors shadow-2xs"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || itensColaborador.length === 0}
              className="px-5 py-2 text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-xl transition-all shadow-sm disabled:opacity-50"
            >
              {loading ? 'Processando...' : 'Confirmar Devolução e Atualizar Estoque'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
